/**
 * CheckInChip — compact pill for the daily +2 pt check-in.
 *
 * Source of truth is the server: `points_log` holds one row per
 * (user, action='daily_checkin', reference_id=<per-day UUID>). The chip
 * queries that row on mount / login / tab focus so the checked-in state
 * syncs across devices. localStorage is only an offline-friendly hint.
 */
import { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Check, Sunrise } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { usePoints } from "@/hooks/usePoints";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const DAILY_CHECKIN_POINTS = 2;
const storageKey = (uid: string) => `gt_daily_checkin:${uid}`;
const todayKey = () => new Date().toISOString().slice(0, 10);

/**
 * add_user_points expects a UUID reference_id. A plain string like
 * "checkin-2026-08-11" makes the RPC fail, so points were silently lost.
 * Build a deterministic, per-day UUID instead (same day = same id = server dedupe).
 */
const checkinReferenceId = (date: string) =>
  `00000000-0000-4000-8000-${date.replace(/-/g, "").padEnd(12, "0")}`;

export function CheckInChip({ className = "" }: { className?: string }) {
  const { user } = useAuth();
  const { awardPoints } = usePoints();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const date = todayKey();

  /** Optimistic/offline hint — never overrides a confirmed server answer. */
  const [localHint, setLocalHint] = useState(false);
  useEffect(() => {
    if (!user?.id) return setLocalHint(false);
    try {
      setLocalHint(localStorage.getItem(storageKey(user.id)) === date);
    } catch { /* ignore */ }
  }, [user?.id, date]);

  const { data: serverClaimed, isLoading } = useQuery({
    queryKey: ["daily-checkin", user?.id, date],
    enabled: !!user?.id,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("points_log")
        .select("id")
        .eq("user_id", user!.id)
        .eq("action_type", "daily_checkin")
        .eq("reference_id", checkinReferenceId(date))
        .maybeSingle();
      if (error) throw error;
      return !!data;
    },
  });

  // Keep the local hint aligned with the server answer (e.g. checked in elsewhere).
  useEffect(() => {
    if (serverClaimed === undefined || !user?.id) return;
    try {
      if (serverClaimed) localStorage.setItem(storageKey(user.id), date);
      else if (localStorage.getItem(storageKey(user.id)) === date)
        localStorage.removeItem(storageKey(user.id));
    } catch { /* ignore */ }
    setLocalHint(!!serverClaimed);
  }, [serverClaimed, user?.id, date]);

  const claimed = serverClaimed ?? localHint;
  const [burstKey, setBurstKey] = useState(0);

  /** Positive-only streak: consecutive check-in days ending today (or yesterday). */
  const { data: streak = 0 } = useQuery({
    queryKey: ["daily-checkin-streak", user?.id, date, claimed],
    enabled: !!user?.id,
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await supabase
        .from("points_log")
        .select("reference_id")
        .eq("user_id", user!.id)
        .eq("action_type", "daily_checkin")
        .order("created_at", { ascending: false })
        .limit(60);
      const days = new Set(
        (data ?? [])
          .map((r: any) => String(r.reference_id ?? "").slice(24, 32))
          .filter((s) => /^\d{8}$/.test(s)),
      );
      const fmt = (d: Date) => d.toISOString().slice(0, 10).replace(/-/g, "");
      const cursor = new Date();
      if (!days.has(fmt(cursor))) cursor.setUTCDate(cursor.getUTCDate() - 1);
      let n = 0;
      while (days.has(fmt(cursor))) {
        n++;
        cursor.setUTCDate(cursor.getUTCDate() - 1);
      }
      return n;
    },
  });

  const claim = useCallback(async () => {
    if (!user?.id || busy || claimed) return;
    setBusy(true);
    const awarded = await awardPoints(
      DAILY_CHECKIN_POINTS,
      "daily_checkin",
      checkinReferenceId(date),
      `Daily check-in (${date})`,
    );
    if (awarded) setBurstKey((k) => k + 1);
    try { localStorage.setItem(storageKey(user.id), date); } catch { /* ignore */ }
    setLocalHint(true);
    queryClient.setQueryData(["daily-checkin", user.id, date], true);
    queryClient.invalidateQueries({ queryKey: ["daily-checkin", user.id, date] });
    setBusy(false);
    toast({
      title: awarded ? "Checked in ✨" : "Already checked in today",
      description: awarded
        ? `+${DAILY_CHECKIN_POINTS} pts added to your total — see you tomorrow!`
        : "Come back tomorrow for your next check-in.",
      duration: 1800,
    });
  }, [user?.id, busy, claimed, awardPoints, toast, date, queryClient]);

  if (!user) return null;

  return (
    <span className="relative inline-flex items-center gap-1.5">
      <Button
        size="sm"
        variant={claimed ? "outline" : "default"}
        onClick={claim}
        disabled={claimed || busy || isLoading}
        className={`h-8 rounded-full px-3 gap-1.5 text-xs font-semibold ${
          claimed
            ? "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:bg-emerald-950/40 dark:border-emerald-900 dark:text-emerald-300"
            : "bg-gradient-to-r from-amber-400 to-orange-500 hover:opacity-90 text-white border-none ring-2 ring-amber-300/60 animate-pulse"
        } ${className}`}
        aria-label={claimed ? "Checked in today" : "Daily check-in for +2 points"}
      >
        {claimed ? (
          <>
            <Check className="h-3.5 w-3.5" />
            <span>Checked in</span>
          </>
        ) : (
          <>
            <Sunrise className="h-3.5 w-3.5" />
            <span>Check in +{DAILY_CHECKIN_POINTS}</span>
          </>
        )}
      </Button>

      {streak >= 2 && (
        <span
          className="inline-flex items-center gap-0.5 rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-bold text-orange-700 dark:bg-orange-950/50 dark:text-orange-300"
          title={`${streak} days in a row — nice momentum!`}
          aria-label={`${streak}-day check-in streak`}
        >
          🔥 {streak}
        </span>
      )}

      {burstKey > 0 && (
        <span key={burstKey} aria-hidden className="pointer-events-none absolute left-10 top-1/2">
          {Array.from({ length: 10 }).map((_, i) => (
            <span
              key={i}
              className="hud-motion absolute h-1.5 w-1.5 rounded-full bg-amber-400"
              style={{
                ["--a" as any]: `${i * 36}deg`,
                animation: "sparkle-burst 0.7s ease-out forwards",
              }}
            />
          ))}
          <span
            className="hud-motion absolute -top-6 left-0 whitespace-nowrap rounded-full bg-amber-500 px-2 py-0.5 text-[11px] font-bold text-white shadow"
            style={{ animation: "floatUp 1.6s ease-out forwards" }}
          >
            +{DAILY_CHECKIN_POINTS} points added!
          </span>
        </span>
      )}
    </span>
  );
}
